import React, {useCallback, useEffect, useMemo, useRef} from "react";
import {Box} from "@material-ui/core";
import {
  FileItem,
  Matter,
  MatterTreeNode as MatterTreeNodeType,
} from "common/types/matter";
import {
  buildTreeStructure,
  computeDefaultCollapsedIds,
  injectPendingUpload,
  promoteTargetMsow,
} from "./utils/tree_builder";
import {MatterTreeNode} from "./matter_tree_node";

const styles = {
  root: {
    display: "flex",
    width: "100%",
    justifyContent: "center",
  },
  content: {
    maxWidth: "900px",
    flex: 1,
    overflow: "auto",
    padding: "8px 0px",
  },
} as const;

interface MatterTreeViewProps {
  matterData: Matter;
  onNodeSelect: (node: MatterTreeNodeType | undefined) => void;
  selectedDocumentId?: number;
  hideEmpty?: boolean;
  displayInDocDetail?: boolean;
  checklistDocumentId?: number;
  onChecklistDocumentSelect?: (documentId: number) => void;
  isViewingMSA?: boolean;
  secondaryDocumentLoading?: boolean;
  onAddPursuantUpload?: (node: MatterTreeNodeType) => void;
  canUploadMsow?: boolean;
  uploadTargetNodeId?: number;
  // True while the user is staging a targeted upload; lets a node treat a row
  // click as "select as upload target" rather than "open document".
  isAddingFiles?: boolean;
  // Pending files are previewed pursuant_to `targetNodeId`. The caller resolves
  // the target (including any "first family by default"); when it is omitted
  // there is nothing to anchor to, so no preview is shown.
  pendingUpload?: {targetNodeId?: number; fileItems: FileItem[]};
  // Remove/restore existing MSA files while staging a new MSA (to "replace").
  onRemoveMsaFile?: (node: MatterTreeNodeType) => void;
  removedMsaIds?: Set<number>;
  canRemoveMsa?: boolean;
}

export const MatterTreeView: React.FC<MatterTreeViewProps> = ({
  matterData,
  onNodeSelect,
  selectedDocumentId,
  hideEmpty = false,
  displayInDocDetail = false,
  checklistDocumentId,
  onChecklistDocumentSelect,
  isViewingMSA = false,
  secondaryDocumentLoading = false,
  onAddPursuantUpload,
  canUploadMsow = false,
  uploadTargetNodeId,
  isAddingFiles = false,
  pendingUpload,
  onRemoveMsaFile,
  removedMsaIds,
  canRemoveMsa = true,
}) => {
  const treeStructure = useMemo(() => {
    let tree = buildTreeStructure(
      matterData.documents,
      matterData.collections,
      hideEmpty,
    );
    if (
      pendingUpload &&
      pendingUpload.fileItems.length > 0 &&
      pendingUpload.targetNodeId !== undefined
    ) {
      tree = injectPendingUpload(
        tree,
        pendingUpload.targetNodeId,
        pendingUpload.fileItems,
      );
    }
    // While adding files to an MSOW, keep it at the top of its MSOW folder.
    if (isAddingFiles) {
      tree = promoteTargetMsow(tree, uploadTargetNodeId);
    }
    return tree;
  }, [
    matterData.collections,
    matterData.documents,
    hideEmpty,
    pendingUpload,
    isAddingFiles,
    uploadTargetNodeId,
  ]);

  // When previewing an upload, collapse the branches that aren't relevant to the
  // selected target so the focus stays where the files will land.
  const hasPendingUpload =
    Boolean(pendingUpload) &&
    (pendingUpload?.fileItems.length ?? 0) > 0 &&
    pendingUpload?.targetNodeId !== undefined;
  const defaultCollapsedIds = useMemo(
    () =>
      hasPendingUpload
        ? computeDefaultCollapsedIds(treeStructure, pendingUpload?.targetNodeId)
        : new Set<number>(),
    [treeStructure, hasPendingUpload, pendingUpload?.targetNodeId],
  );
  // Switching the upload target should re-apply the default collapse state.
  // Passing this down lets each node reset its expand state on target change
  // (animated), while manual expand/collapse still persists within one target.
  const collapseKey: number | string = hasPendingUpload
    ? pendingUpload?.targetNodeId ?? "none"
    : "none";

  // The newest MSA's upload time. A SOW created before it was analysed against an
  // older MSA (old SOWs aren't re-analysed), so it's shown as stale.
  const newestMsaCreationDate = useMemo(() => {
    const msaDates = (matterData.documents ?? [])
      .filter(doc => doc.document_type_id === 1 && doc.creation_date)
      .map(doc => doc.creation_date);
    return msaDates.length > 0
      ? msaDates.reduce((a, b) => (new Date(a) > new Date(b) ? a : b))
      : undefined;
  }, [matterData.documents]);

  const handleNodeSelect = (node: MatterTreeNodeType) => {
    onNodeSelect(node);
  };

  // Keep the relevant part of the tree in view whenever its height changes —
  // expanding or collapsing a section, or clicking a file (which retargets the
  // upload and re-applies the default collapse) — so the user isn't left scrolled
  // past the content. A ResizeObserver catches every such change centrally rather
  // than wiring each action up by hand.
  const contentRef = useRef<HTMLDivElement>(null);
  const treeEndRef = useRef<HTMLDivElement>(null);
  // Set by a node when the user expands/collapses it or reveals hidden siblings,
  // so the resulting height change scrolls to *that* node's new content rather
  // than somewhere else. Consumed (and cleared) by the next settle.
  const requestedScrollRef = useRef<HTMLElement | null>(null);
  const handleRequestScroll = useCallback((element: HTMLElement | null) => {
    requestedScrollRef.current = element;
  }, []);

  // Choose what to bring on-screen after a height change:
  // 1. an element a node explicitly asked to reveal (expand/collapse/reveal), else
  // 2. the pending upload region (added file(s) + "Click to expand …" line, both
  //    tagged `data-scroll-anchor`) while previewing a targeted upload — this
  //    avoids overshooting to the very bottom past the files being added, else
  // 3. the tree end.
  const scrollIntoView = useCallback(() => {
    requestAnimationFrame(() => {
      const requested = requestedScrollRef.current;
      if (requested) {
        requestedScrollRef.current = null;
        requested.scrollIntoView({block: "nearest", behavior: "smooth"});
        return;
      }
      const anchors = contentRef.current?.querySelectorAll<HTMLElement>(
        "[data-scroll-anchor]",
      );
      const target =
        anchors && anchors.length > 0
          ? anchors[anchors.length - 1]
          : treeEndRef.current;
      target?.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    });
  }, []);

  useEffect(() => {
    const el = contentRef.current;
    if (!el || typeof ResizeObserver === "undefined") {
      return undefined;
    }
    // Only react to height changes (collapse/expand), not width (e.g. a window
    // resize), and skip the first measurement so we don't scroll on mount.
    // The height animates over several frames (the Collapse transition), so
    // debounce: wait for it to settle, then do a single smooth scroll rather
    // than restarting one on every frame.
    let lastHeight: number | undefined;
    let settleTimer: ReturnType<typeof setTimeout> | undefined;
    const observer = new ResizeObserver(() => {
      const height = el.offsetHeight;
      if (lastHeight !== undefined && height !== lastHeight) {
        clearTimeout(settleTimer);
        settleTimer = setTimeout(scrollIntoView, 150);
      }
      lastHeight = height;
    });
    observer.observe(el);
    return () => {
      clearTimeout(settleTimer);
      observer.disconnect();
    };
  }, [scrollIntoView]);

  return (
    <div style={styles.root}>
      <Box ref={contentRef} style={styles.content}>
        {treeStructure.map(node => (
          <MatterTreeNode
            key={`${node.type}-${node.id}`}
            node={node}
            level={0}
            defaultCollapsedIds={defaultCollapsedIds}
            collapseKey={collapseKey}
            onNodeSelect={handleNodeSelect}
            selectedNodeId={selectedDocumentId}
            displayInDocDetail={displayInDocDetail}
            checklistDocumentId={checklistDocumentId}
            onChecklistDocumentSelect={onChecklistDocumentSelect}
            isViewingMSA={isViewingMSA}
            secondaryDocumentLoading={secondaryDocumentLoading}
            onAddPursuantUpload={onAddPursuantUpload}
            canUploadMsow={canUploadMsow}
            uploadTargetNodeId={uploadTargetNodeId}
            isAddingFiles={isAddingFiles}
            onRequestScroll={handleRequestScroll}
            onRemoveMsaFile={onRemoveMsaFile}
            removedMsaIds={removedMsaIds}
            canRemoveMsa={canRemoveMsa}
            newestMsaCreationDate={newestMsaCreationDate}
          />
        ))}
        {/* Anchor scrolled into view after a height change to keep it visible. */}
        <div ref={treeEndRef} />
      </Box>
    </div>
  );
};
